<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { watch } from "vue";
import z from "zod";

import { IEditSiteDetailsFormExpose } from "@/components/tools/site-details/edit-site-details/edit-site-details-form.types";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import {
  EElectricityTarif,
  TEditSiteConsumptionInfosBody,
  TSiteConsumptionInfos,
} from "@/data/api/kili/consumption/types/consumption-infos.type";
import { useTypedIntl } from "@/locales/typed-intl";

import InputText from "primevue/inputtext";

import LocalizedInputNumber from "@/components/ui/inputs/localized-input-number.vue";

interface IProps {
  siteName?: string;
  siteConsumptionInfos?: TSiteConsumptionInfos;
  isLoading?: boolean;
}

const props = defineProps<IProps>();

const { t, k } = useTypedIntl();

const formSchema = z.object({
  siteArea: z.number().nullable(),
  [EElectricityTarif.OFF_PEAK_HOUR]: z.number().nullable(),
  [EElectricityTarif.PEAK_HOUR]: z.number().nullable(),
});

const { handleSubmit, errors, defineField, setFieldValue } = useForm({
  validationSchema: toTypedSchema(formSchema),
  initialValues: {
    siteArea: null,
    [EElectricityTarif.OFF_PEAK_HOUR]: null,
    [EElectricityTarif.PEAK_HOUR]: null,
  },
});

const [siteArea] = defineField("siteArea");
const [hc] = defineField(EElectricityTarif.OFF_PEAK_HOUR);
const [hp] = defineField(EElectricityTarif.PEAK_HOUR);

const submit = handleSubmit((values) => {
  const payload: TEditSiteConsumptionInfosBody = {
    area: values.siteArea,
    peakHoursPrice: values.peakHours,
    offPeakHoursPrice: values.offPeakHours,
  };

  return payload;
});

watch(
  () => props.siteConsumptionInfos,
  (newValue) => {
    if (!newValue) return;

    setFieldValue("siteArea", newValue.siteArea);
    setFieldValue(EElectricityTarif.OFF_PEAK_HOUR, newValue.electricityPrices[EElectricityTarif.OFF_PEAK_HOUR]);
    setFieldValue(EElectricityTarif.PEAK_HOUR, newValue.electricityPrices[EElectricityTarif.PEAK_HOUR]);
  },
  { immediate: true },
);

defineExpose<IEditSiteDetailsFormExpose>({
  submit,
});
</script>

<template>
  <form
    class="flex flex-col gap-10"
    :data-test-id="DATA_TEST_IDS.components.editSiteDetailsForm.component"
    @submit="submit"
  >
    <div class="flex flex-col gap-6">
      <small class="uppercase">
        {{ t(k.tools.siteManagement.siteDetails.edition.informations) }}
      </small>

      <section class="flex flex-col gap-2">
        <label
          id="site-name-label"
          class="font-medium"
          for="site-name"
          :data-test-id="DATA_TEST_IDS.components.editSiteDetailsForm.siteName.label"
        >
          {{ t(k.tools.siteManagement.siteDetails.edition.siteName) }}
        </label>
        <InputText
          v-model="props.siteName"
          disabled
          aria-labelledby="site-name-label"
          input-id="site-name"
          :pt="{
            root: {
              ['data-test-id']: DATA_TEST_IDS.components.editSiteDetailsForm.siteName.input,
            },
          }"
        />
      </section>

      <section class="flex flex-col gap-2">
        <label
          id="site-area-label"
          class="font-medium"
          :data-test-id="DATA_TEST_IDS.components.editSiteDetailsForm.siteArea.label"
          for="site-area"
        >
          {{ t(k.tools.siteManagement.siteDetails.surfaceArea) }}
        </label>
        <LocalizedInputNumber
          v-model="siteArea"
          aria-labelledby="site-area-label"
          autofocus
          :disabled="props.isLoading"
          :error="errors['siteArea']"
          input-id="site-area"
          :invalid="!!errors['siteArea']"
          :max-fraction-digits="4"
          :placeholder="t(k.tools.siteManagement.siteDetails.edition.placeholder.surfaceArea)"
          :pt="{
            root: {
              ['data-test-id']: DATA_TEST_IDS.components.editSiteDetailsForm.siteArea.input,
            },
          }"
          suffix=" m²"
        />
      </section>
    </div>

    <div class="flex flex-col gap-6">
      <small class="uppercase">
        {{ t(k.tools.siteManagement.siteDetails.kWhCost) }}
      </small>

      <section class="flex flex-col gap-2">
        <label
          id="hc-label"
          class="font-medium"
          :data-test-id="DATA_TEST_IDS.components.editSiteDetailsForm.offPeakHourTarif.label"
          for="hc"
        >
          {{ t(k.tools.siteManagement.siteDetails.edition.label.hc) }}
        </label>
        <LocalizedInputNumber
          v-model="hc"
          aria-labelledby="hc-label"
          data-test-id="hc-number-input"
          :disabled="props.isLoading"
          :error="errors[EElectricityTarif.OFF_PEAK_HOUR]"
          input-id="hc"
          :invalid="!!errors[EElectricityTarif.OFF_PEAK_HOUR]"
          :max-fraction-digits="4"
          :placeholder="t(k.tools.siteManagement.siteDetails.edition.placeholder.hc)"
          :pt="{
            root: {
              ['data-test-id']: DATA_TEST_IDS.components.editSiteDetailsForm.offPeakHourTarif.input,
            },
          }"
          suffix=" €"
        />
      </section>

      <section class="flex flex-col gap-2">
        <label
          id="hp-label"
          class="font-medium"
          :data-test-id="DATA_TEST_IDS.components.editSiteDetailsForm.peakHourTarif.label"
          for="hp"
        >
          {{ t(k.tools.siteManagement.siteDetails.edition.label.hp) }}
        </label>
        <LocalizedInputNumber
          v-model="hp"
          aria-labelledby="hp-label"
          data-test-id="hp-number-input"
          :disabled="props.isLoading"
          :error="errors[EElectricityTarif.PEAK_HOUR]"
          input-id="hp"
          :invalid="!!errors[EElectricityTarif.PEAK_HOUR]"
          :max-fraction-digits="4"
          :placeholder="t(k.tools.siteManagement.siteDetails.edition.placeholder.hp)"
          :pt="{
            root: {
              ['data-test-id']: DATA_TEST_IDS.components.editSiteDetailsForm.peakHourTarif.input,
            },
          }"
          suffix=" €"
        />
      </section>
    </div>
  </form>
</template>
